import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Trash2, Edit, X, LogOut, Moon, Sun, Save, Plus, Users, UserPlus, ToggleLeft, ToggleRight, DollarSign } from 'lucide-react';

const STATUSES = ['Pending', 'Reviewed', 'Scheduled', 'Completed', 'Archived'];
const STATUS_COLORS = {
    Pending:   { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
    Reviewed:  { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
    Scheduled: { bg: '#ccfdf7', color: '#0f766e', border: '#5eead4' },
    Completed: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
    Archived:  { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
};

// Session heartbeat interval (ms)
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes


const EMPTY_FORM = {
    date: new Date().toISOString().split('T')[0], referringDr: '', phone: '',
    patientName: '', patientDOB: '', patientPhone: '', patientEmail: '',
    procedures: [], selectedTeeth: [],
    dentalImplants: { area: '', sinusLift: false, boneGraft: false, gingivalGraft: false, other: '' },
    comments: '', callBeforeTreatment: false, radiographsSent: false,
    status: 'Pending', internalNotes: '',
};

const PROCEDURES_LIST = [
    'Extraction(s)', 'Wisdom Teeth Extraction', 'Bone Grafting', 'Expose & Bond',
    'Facial Trauma', 'Frenectomy', 'Gingivectomy', 'Infection Treatment',
    'Pre-Prosthetic Surgery', 'PRP/PRF', 'Orofacial/TMJ Pain', 'Oral Pathology',
    'Trigeminal Neuralgia', 'Tori Reduction', 'Biopsy/Pathology',
];

export default function DeskDashboard() {
    const navigate = useNavigate();
    const [referrals, setReferrals]       = useState([]);
    const [search, setSearch]             = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [loading, setLoading]           = useState(true);
    const [darkMode, setDarkMode]         = useState(false);
    const [sessionVerified, setSessionVerified] = useState(false);

    // Modals
    const [viewModal, setViewModal]       = useState(null);   // referral object
    const [editModal, setEditModal]       = useState(null);   // referral object or 'new'
    const [editForm, setEditForm]         = useState(EMPTY_FORM);
    const [editSaving, setEditSaving]     = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null); // referral _id

    const [userName, setUserName] = useState('Desk');
    const [activeTab, setActiveTab] = useState('referrals'); // referrals | staff
    const [staffList, setStaffList]               = useState([]);
    const [staffLoading, setStaffLoading]         = useState(false);
    const [inviteModal, setInviteModal]           = useState(false);
    const [inviteForm, setInviteForm]             = useState({ name: '', email: '', role: 'doctor' });
    const [inviteSending, setInviteSending]       = useState(false);
    const [inviteMsg, setInviteMsg]               = useState('');

    const [payments, setPayments]                 = useState([]);
    const [paymentsLoading, setPaymentsLoading]   = useState(false);

    // ── Security: authenticated fetch wrapper ─────────────────────────────────
    // Auto-logout on 401/403 responses
    const apiFetch = async (url, opts = {}) => {
        const res = await fetch(url, {
            ...opts,
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
        });
        if (res.status === 401 || res.status === 403) {
            logout();
            throw new Error('Session expired or unauthorized');
        }
        return res;
    };

    // ── Security: verify session + role on mount ──────────────────────────────
    useEffect(() => {
        const verifySession = async () => {
            try {
                const res = await fetch('/api/auth/me', { credentials: 'include' });
                if (!res.ok) { logout(); return; }
                const data = await res.json();
                if (data.role !== 'desk') {
                    // Wrong role — redirect to correct dashboard
                    navigate(data.role === 'doctor' ? '/doctor' : '/login', { replace: true });
                    return;
                }
                setUserName(data.name || 'Desk');
                localStorage.setItem('trf_role', data.role);
                localStorage.setItem('trf_name', data.name);
                setSessionVerified(true);
            } catch {
                logout();
            }
        };
        verifySession();

        // ── Security: periodic session heartbeat ──────────────────────────────
        const heartbeat = setInterval(async () => {
            try {
                const res = await fetch('/api/auth/me', { credentials: 'include' });
                if (!res.ok) logout();
            } catch { /* network errors handled gracefully */ }
        }, SESSION_CHECK_INTERVAL);
        return () => clearInterval(heartbeat);
    }, []);

    const fetchReferrals = async () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (statusFilter) params.set('status', statusFilter);
        if (search) params.set('search', search);
        try {
            const res = await apiFetch(`/api/referrals?${params}`);
            setReferrals(await res.json());
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const fetchStaff = async () => {
        setStaffLoading(true);
        try {
            const res = await apiFetch('/api/invite/users');
            if (res.ok) setStaffList(await res.json());
        } finally { setStaffLoading(false); }
    };

    const fetchPayments = async () => {
        setPaymentsLoading(true);
        try {
            const res = await apiFetch('/api/payments');
            if (res.ok) setPayments(await res.json());
        } finally { setPaymentsLoading(false); }
    };

    useEffect(() => { if (sessionVerified) fetchReferrals(); }, [search, statusFilter, sessionVerified]);
    useEffect(() => { if (sessionVerified && activeTab === 'staff') fetchStaff(); }, [activeTab, sessionVerified]);
    useEffect(() => { if (sessionVerified && activeTab === 'payments') fetchPayments(); }, [activeTab, sessionVerified]);

    const logout = async () => {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
        localStorage.removeItem('trf_role');
        localStorage.removeItem('trf_name');
        navigate('/login');
    };

    const sendInvite = async () => {
        setInviteSending(true); setInviteMsg('');
        try {
            const res = await apiFetch('/api/invite', { method: 'POST', body: JSON.stringify(inviteForm) });
            const data = await res.json();
            setInviteMsg(res.ok ? `✅ ${data.message}` : `❌ ${data.message}`);
            if (res.ok) { setInviteForm({ name: '', email: '', role: 'doctor' }); fetchStaff(); }
        } finally { setInviteSending(false); }
    };

    const toggleActive = async (userId, current) => {
        await apiFetch(`/api/invite/users/${userId}`, { method: 'PATCH', body: JSON.stringify({ isActive: !current }) });
        fetchStaff();
    };

    const openEdit = (r) => {
        setEditForm(r ? { ...EMPTY_FORM, ...r } : { ...EMPTY_FORM });
        setEditModal(r || 'new');
    };

    const saveEdit = async () => {
        setEditSaving(true);
        try {
            const isNew = editModal === 'new';
            const res = await apiFetch(
                isNew ? '/api/referrals' : `/api/referrals/${editModal._id}`,
                { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(editForm) }
            );
            if (res.ok) { setEditModal(null); fetchReferrals(); }
        } finally { setEditSaving(false); }
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        await apiFetch(`/api/referrals/${deleteConfirm}`, { method: 'DELETE' });
        setDeleteConfirm(null);
        fetchReferrals();
    };

    const quickStatus = async (id, status) => {
        await apiFetch(`/api/referrals/${id}`, { method: 'PUT', body: JSON.stringify({ status }) });
        fetchReferrals();
    };

    const setField = (k, v) => setEditForm(p => ({ ...p, [k]: v }));
    const toggleProc = (p) => setEditForm(prev => ({
        ...prev,
        procedures: prev.procedures.includes(p) ? prev.procedures.filter(x => x !== p) : [...prev.procedures, p],
    }));

    // ── Security: don't render until session is verified ──────────────────
    if (!sessionVerified) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--off-white)' }}>
                <span className="trf-spinner" style={{ width: '2rem', height: '2rem' }} />
            </div>
        );
    }

    return (
        <div className={`app${darkMode ? ' dark' : ''}`}>
            {/* Nav */}
            <nav className="dash-nav">
                <div className="dash-nav-brand">
                    <span style={{ color: '#fff' }}>Titanium</span>&nbsp;
                    <span style={{ color: 'var(--teal)' }}>Surgical Arts</span>
                    <span className="dash-nav-role">Desk Staff</span>
                </div>
                <div className="dash-nav-actions">
                    <span className="dash-nav-user">{userName}</span>
                    <button className="dash-icon-btn" onClick={() => setDarkMode(d => !d)} aria-label="Toggle dark mode">
                        {darkMode ? <Sun size={16} /> : <Moon size={16} />}
                    </button>
                    <button className="dash-icon-btn" onClick={logout} aria-label="Log out">
                        <LogOut size={16} />
                    </button>
                </div>
            </nav>

            <div className="dash-body">
                {/* Tabs */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--gray-200)', paddingBottom: '0' }}>
                    {[{ id: 'referrals', label: 'Referrals', Icon: FileText }, { id: 'payments', label: 'Payments', Icon: DollarSign }, { id: 'staff', label: 'Staff Management', Icon: Users }].map(({ id, label, Icon }) => (
                        <button key={id} onClick={() => setActiveTab(id)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: activeTab === id ? 'var(--teal)' : 'var(--text-muted)', borderBottom: activeTab === id ? '2px solid var(--teal)' : '2px solid transparent', marginBottom: '-2px', transition: 'color 0.15s' }}>
                            <Icon size={14} />{label}
                        </button>
                    ))}
                </div>

                {activeTab === 'referrals' && (<>
                <div className="dash-toolbar">
                    <h1 className="dash-page-title">Referral Management</h1>
                    <div className="dash-toolbar-right">
                        <div className="dash-search-wrap">
                            <Search size={15} className="dash-search-icon" />
                            <input className="trf-input dash-search" placeholder="Search patient or doctor…" value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        <select className="trf-input" style={{ width: 'auto' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="">All Statuses</option>
                            {STATUSES.map(s => <option key={s}>{s}</option>)}
                        </select>
                        <button className="trf-btn-submit" style={{ width: 'auto', padding: '0.55rem 1.1rem', fontSize: '0.82rem' }} onClick={() => openEdit(null)}>
                            <Plus size={15} /> New Referral
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="dash-stats">
                    {Object.entries(STATUS_COLORS).map(([s, c]) => (
                        <button key={s} className="dash-stat-pill" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }} onClick={() => setStatusFilter(statusFilter === s ? '' : s)}>
                            {s}: <strong>{referrals.filter(r => r.status === s).length}</strong>
                        </button>
                    ))}
                </div>

                {/* Table */}
                <div className="trf-card" style={{ padding: 0, overflow: 'hidden' }}>
                    {loading ? <div className="dash-empty">Loading…</div> : referrals.length === 0 ? <div className="dash-empty">No referrals found.</div> : (
                        <table className="dash-table">
                            <thead>
                                <tr>
                                    <th>Date</th><th>Patient</th><th>Referring Dr.</th>
                                    <th>Procedures</th><th>Status</th><th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {referrals.map(r => {
                                    const sc = STATUS_COLORS[r.status] || STATUS_COLORS.Pending;
                                    return (
                                        <tr key={r._id}>
                                            <td>{r.date || '—'}</td>
                                            <td><strong>{r.patientName}</strong><br /><span className="dash-sub">{r.patientPhone}</span></td>
                                            <td>{r.referringDr}</td>
                                            <td>{r.procedures?.length || 0} procedure{r.procedures?.length !== 1 ? 's' : ''}</td>
                                            <td>
                                                <select
                                                    className="dash-status-select"
                                                    style={{ background: sc.bg, color: sc.color, borderColor: sc.border }}
                                                    value={r.status}
                                                    onChange={e => quickStatus(r._id, e.target.value)}
                                                >
                                                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                                                </select>
                                            </td>
                                            <td style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                                                <button className="dash-action-btn" onClick={() => setViewModal(r)} title="View"><FileText size={15} /></button>
                                                <button className="dash-action-btn" onClick={() => openEdit(r)} title="Edit"><Edit size={15} /></button>
                                                <button className="dash-action-btn danger" onClick={() => setDeleteConfirm(r._id)} title="Delete"><Trash2 size={15} /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                </>)}

                {/* ── Payments Tab ── */}
                {activeTab === 'payments' && (
                    <>
                        <div className="dash-toolbar">
                            <h1 className="dash-page-title">Payment History</h1>
                        </div>
                        <div className="trf-card" style={{ padding: 0, overflow: 'hidden' }}>
                            {paymentsLoading ? <div className="dash-empty">Loading payments…</div> : payments.length === 0 ? <div className="dash-empty">No payments found.</div> : (
                                <table className="dash-table">
                                    <thead><tr><th>Date</th><th>Patient</th><th>Amount</th><th>Procedure</th><th>Status</th><th>Clover ID / Invoice</th></tr></thead>
                                    <tbody>
                                        {payments.map(p => (
                                            <tr key={p._id}>
                                                <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                                                <td><strong>{p.patientName}</strong><br /><span className="dash-sub">{p.patientEmail}</span></td>
                                                <td style={{ color: '#166534', fontWeight: 'bold' }}>${p.amount.toFixed(2)}</td>
                                                <td>{p.procedureType}</td>
                                                <td><span className="dash-badge" style={{ background: p.status === 'SUCCESS' ? '#dcfce7' : '#fef2f2', color: p.status === 'SUCCESS' ? '#166534' : '#dc2626', borderColor: p.status === 'SUCCESS' ? '#86efac' : '#fecaca' }}>{p.status}</span></td>
                                                <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{p.cloverChargeId}<br />{p.invoiceNumber ? `INV: ${p.invoiceNumber}` : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}

                {/* ── Staff Management Tab ── */}
                {activeTab === 'staff' && (
                    <>
                        <div className="dash-toolbar">
                            <h1 className="dash-page-title">Staff Management</h1>
                            <button className="trf-btn-submit" style={{ width: 'auto', padding: '0.55rem 1.1rem', fontSize: '0.82rem' }} onClick={() => { setInviteModal(true); setInviteMsg(''); }}>
                                <UserPlus size={15} /> Invite Staff
                            </button>
                        </div>
                        <div className="trf-card" style={{ padding: 0, overflow: 'hidden' }}>
                            {staffLoading ? <div className="dash-empty">Loading staff…</div> : staffList.length === 0 ? <div className="dash-empty">No staff accounts found.</div> : (
                                <table className="dash-table">
                                    <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last Login</th><th>Actions</th></tr></thead>
                                    <tbody>
                                        {staffList.map(u => (
                                            <tr key={u._id}>
                                                <td><strong>{u.name}</strong></td>
                                                <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{u.email}</td>
                                                <td><span className="dash-badge" style={{ background: u.role === 'desk' ? '#dbeafe' : '#ccfdf7', color: u.role === 'desk' ? '#1e40af' : '#0f766e', borderColor: u.role === 'desk' ? '#93c5fd' : '#5eead4', textTransform: 'capitalize' }}>{u.role}</span></td>
                                                <td><span className="dash-badge" style={{ background: u.isActive ? '#dcfce7' : '#fef2f2', color: u.isActive ? '#166534' : '#dc2626', borderColor: u.isActive ? '#86efac' : '#fecaca' }}>{u.isActive ? 'Active' : 'Inactive'}</span></td>
                                                <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'}</td>
                                                <td>
                                                    <button className={`dash-action-btn${u.isActive ? ' danger' : ''}`} title={u.isActive ? 'Deactivate' : 'Activate'} onClick={() => toggleActive(u._id, u.isActive)}>
                                                        {u.isActive ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* ── View Modal ── */}
            {viewModal && (
                <div className="dash-overlay" onClick={() => setViewModal(null)}>
                    <div className="dash-drawer" onClick={e => e.stopPropagation()}>
                        <div className="dash-drawer-header">
                            <div>
                                <h2 className="dash-drawer-title">{viewModal.patientName}</h2>
                                <p className="dash-drawer-sub">{new Date(viewModal.submittedAt).toLocaleString()}</p>
                            </div>
                            <button className="dash-icon-btn" onClick={() => setViewModal(null)}><X size={18} /></button>
                        </div>
                        <div className="dash-drawer-body">
                            <div className="dash-detail-grid">
                                <div><span className="dash-detail-label">DOB</span><span>{viewModal.patientDOB || '—'}</span></div>
                                <div><span className="dash-detail-label">Phone</span><span>{viewModal.patientPhone || '—'}</span></div>
                                <div><span className="dash-detail-label">Email</span><span>{viewModal.patientEmail || '—'}</span></div>
                                <div><span className="dash-detail-label">Referring Dr.</span><span>{viewModal.referringDr}</span></div>
                                <div><span className="dash-detail-label">Dr. Phone</span><span>{viewModal.phone || '—'}</span></div>
                                <div><span className="dash-detail-label">Status</span><span className="dash-badge" style={{ background: STATUS_COLORS[viewModal.status]?.bg, color: STATUS_COLORS[viewModal.status]?.color, borderColor: STATUS_COLORS[viewModal.status]?.border }}>{viewModal.status}</span></div>
                                <div><span className="dash-detail-label">Radiographs</span><span>{viewModal.radiographsSent ? 'Sent' : 'Not sent'}</span></div>
                                <div><span className="dash-detail-label">Call Before</span><span>{viewModal.callBeforeTreatment ? 'Yes' : 'No'}</span></div>
                            </div>
                            {viewModal.procedures?.length > 0 && <div className="dash-detail-section"><span className="dash-detail-label">Procedures</span><div className="dash-tag-list">{viewModal.procedures.map(p => <span key={p} className="dash-tag">{p}</span>)}</div></div>}
                            {viewModal.selectedTeeth?.length > 0 && <div className="dash-detail-section"><span className="dash-detail-label">Teeth</span><div className="dash-tag-list">{viewModal.selectedTeeth.map(t => <span key={t} className="dash-tag">#{t}</span>)}</div></div>}
                            {viewModal.comments && <div className="dash-detail-section"><span className="dash-detail-label">Comments</span><p className="dash-detail-text">{viewModal.comments}</p></div>}
                            {viewModal.internalNotes && <div className="dash-detail-section"><span className="dash-detail-label">Internal Notes</span><p className="dash-detail-text">{viewModal.internalNotes}</p></div>}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Edit / New Modal ── */}
            {editModal !== null && (
                <div className="dash-overlay" onClick={() => setEditModal(null)}>
                    <div className="dash-drawer dash-drawer-wide" onClick={e => e.stopPropagation()}>
                        <div className="dash-drawer-header">
                            <h2 className="dash-drawer-title">{editModal === 'new' ? 'New Referral' : `Edit — ${editModal.patientName}`}</h2>
                            <button className="dash-icon-btn" onClick={() => setEditModal(null)}><X size={18} /></button>
                        </div>
                        <div className="dash-drawer-body">
                            <div className="dash-edit-grid">
                                {/* Status + Notes */}
                                <div className="dash-edit-full">
                                    <label className="trf-label">Status</label>
                                    <select className="trf-input" value={editForm.status} onChange={e => setField('status', e.target.value)} style={{ width: 'auto' }}>
                                        {STATUSES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </div>
                                {/* Referring Dr */}
                                <div><label className="trf-label">Date</label><input type="date" className="trf-input" value={editForm.date} onChange={e => setField('date', e.target.value)} /></div>
                                <div><label className="trf-label">Referring Doctor <span className="trf-required">*</span></label><input type="text" className="trf-input" value={editForm.referringDr} onChange={e => setField('referringDr', e.target.value)} /></div>
                                <div><label className="trf-label">Dr. Phone</label><input type="tel" className="trf-input" value={editForm.phone} onChange={e => setField('phone', e.target.value)} /></div>
                                {/* Patient */}
                                <div><label className="trf-label">Patient Name <span className="trf-required">*</span></label><input type="text" className="trf-input" value={editForm.patientName} onChange={e => setField('patientName', e.target.value)} /></div>
                                <div><label className="trf-label">Date of Birth</label><input type="date" className="trf-input" value={editForm.patientDOB} onChange={e => setField('patientDOB', e.target.value)} /></div>
                                <div><label className="trf-label">Patient Phone</label><input type="tel" className="trf-input" value={editForm.patientPhone} onChange={e => setField('patientPhone', e.target.value)} /></div>
                                <div><label className="trf-label">Patient Email</label><input type="email" className="trf-input" value={editForm.patientEmail} onChange={e => setField('patientEmail', e.target.value)} /></div>
                                {/* Procedures */}
                                <div className="dash-edit-full">
                                    <label className="trf-label">Procedures</label>
                                    <div className="trf-grid-3" style={{ marginTop: '0.4rem' }}>
                                        {PROCEDURES_LIST.map(p => (
                                            <label key={p} className={`trf-check-card${editForm.procedures.includes(p) ? ' checked' : ''}`}>
                                                <input type="checkbox" checked={editForm.procedures.includes(p)} onChange={() => toggleProc(p)} />{p}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                {/* Comments + Notes */}
                                <div className="dash-edit-full"><label className="trf-label">Comments</label><textarea className="trf-input" rows={2} value={editForm.comments} onChange={e => setField('comments', e.target.value)} style={{ resize: 'vertical' }} /></div>
                                <div className="dash-edit-full"><label className="trf-label">Internal Notes</label><textarea className="trf-input" rows={2} value={editForm.internalNotes} onChange={e => setField('internalNotes', e.target.value)} style={{ resize: 'vertical' }} /></div>
                                {/* Checkboxes */}
                                <div className="dash-edit-full" style={{ display: 'flex', gap: '1rem' }}>
                                    <label className={`trf-check-card${editForm.callBeforeTreatment ? ' checked' : ''}`}><input type="checkbox" checked={editForm.callBeforeTreatment} onChange={e => setField('callBeforeTreatment', e.target.checked)} />Call Before Treatment</label>
                                    <label className={`trf-check-card${editForm.radiographsSent ? ' checked' : ''}`}><input type="checkbox" checked={editForm.radiographsSent} onChange={e => setField('radiographsSent', e.target.checked)} />Radiographs Sent</label>
                                </div>
                            </div>
                            <button className="trf-btn-submit" style={{ marginTop: '1.5rem' }} onClick={saveEdit} disabled={editSaving}>
                                {editSaving ? <><span className="trf-spinner" /> Saving…</> : <><Save size={14} /> {editModal === 'new' ? 'Create Referral' : 'Save Changes'}</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Delete Confirm ── */}
            {deleteConfirm && (
                <div className="dash-overlay" onClick={() => setDeleteConfirm(null)}>
                    <div className="trf-modal-box" onClick={e => e.stopPropagation()}>
                        <div className="trf-modal-icon error"><Trash2 size={44} /></div>
                        <p className="trf-modal-title">Delete Referral?</p>
                        <p className="trf-modal-message">This action cannot be undone.</p>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button className="trf-modal-close" style={{ background: '#e53e3e', flex: 1 }} onClick={confirmDelete}>Delete</button>
                            <button className="trf-modal-close" style={{ background: 'var(--gray-400)', flex: 1 }} onClick={() => setDeleteConfirm(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Invite Modal ── */}
            {inviteModal && (
                <div className="dash-overlay" onClick={() => setInviteModal(false)}>
                    <div className="trf-modal-box" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
                        <p className="trf-modal-title" style={{ marginBottom: '0.25rem' }}>Invite Staff Member</p>
                        <p className="trf-modal-message" style={{ marginBottom: '1.25rem' }}>
                            Only <strong>@titanium-surgicalarts.com</strong> addresses are allowed.
                        </p>
                        <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                            <div><label className="trf-label">Full Name</label><input className="trf-input" type="text" placeholder="Dr. Tabarini" value={inviteForm.name} onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))} /></div>
                            <div><label className="trf-label">Email Address</label><input className="trf-input" type="email" placeholder="name@titanium-surgicalarts.com" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} /></div>
                            <div>
                                <label className="trf-label">Role</label>
                                <select className="trf-input" value={inviteForm.role} onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))}>
                                    <option value="doctor">Doctor</option>
                                    <option value="desk">Desk Staff</option>
                                </select>
                            </div>
                        </div>
                        {inviteMsg && (
                            <p style={{ marginTop: '0.85rem', fontSize: '0.85rem', color: inviteMsg.startsWith('✅') ? 'var(--teal)' : '#dc2626', textAlign: 'left' }}>{inviteMsg}</p>
                        )}
                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                            <button className="trf-modal-close" style={{ flex: 1 }} onClick={sendInvite} disabled={inviteSending}>
                                {inviteSending ? 'Sending…' : 'Send Invite'}
                            </button>
                            <button className="trf-modal-close" style={{ background: 'var(--gray-400)', flex: 1 }} onClick={() => setInviteModal(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>

    );
}
